<template xmlns="http://www.w3.org/1999/html">
    <LayoutList>
        <template v-if="resource && !$wait.is('fetching view resource')">
            <template slot="header" v-if="resource.eventName && resource.id">
                <h1>{{ resource.eventName || '&nbsp;' }}</h1>
                <router-link :to="{ name: 'edit-event', params: { event_id: resource.id } }" class="btn btn--primary">
                    Edit
                </router-link>
            </template>
            <section class="event-info">
                <ul class="grid post-details">
                    <li v-if="resource.start" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">Start Date</span>
                        <span>{{ resource.start.dateTimeString }}</span>
                    </li>
                    <li v-if="resource.end" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">End Date</span>
                        <span>{{ resource.end.dateTimeString }}</span>
                    </li>
                    <li v-if="resource.location" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">Location</span>
                        <span>{{ resource.location }}</span>
                    </li>
                </ul>
            </section>
            <section class="bilateral-info">
                <ul class="grid post-details">
                    <li v-if="resource.bilateralStart" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">Bilateral Start Date</span>
                        <span>{{ resource.bilateralStart.displayDate }}, {{ resource.bilateralStart.displayTime }}</span>
                    </li>
                    <li v-if="resource.bilateralEnd" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">Bilateral End Date</span>
                        <span>{{ resource.bilateralEnd.displayDate }}, {{ resource.bilateralEnd.displayTime }}</span>
                    </li>
                    <li v-if="resource.bilateralMeetingRoom" class="col-12 col-md-2 col-bleed-y detail">
                        <span class="label">Meeting Room</span>
                        <span>{{ resource.bilateralMeetingRoom }}</span>
                    </li>
                </ul>
            </section>
            <div class="create" v-if="resource.id">
                <router-link :to="{ name: 'create-activity', params: { eventId: resource.id } }" class="btn btn--primary">
                    Create Activity
                </router-link>
            </div>

            <div v-if="resource.activities">
                <div class="activity-date" v-for="(activities, date) in activitiesByDate" :key="date">
                    <h2>{{ date }}</h2>
                    <section class="grid">
                        <ActivityCard
                            v-for="activity in activities" :key="activity.id"
                            :id="activity.id"
                            :activityName="activity.activityName"
                            :activityType="activity.activityTypeFormatted"
                            :backgroundUrl="activity.backgroundUrl"
                            :start="activity.start.dateTimeString"
                            :end="activity.end.dateTimeString"
                        />
                    </section>
                </div>
                <div class="other-activities" v-if="otherActivities.length > 0">
                    <h2>Other Activities</h2>
                    <div class="activity-date">
                        <section class="grid">
                            <ActivityCard
                                v-for="otherActivity in otherActivities" :key="otherActivity.id"
                                :id="otherActivity.id"
                                :activityName="otherActivity.activityName"
                                :activityType="otherActivity.activityTypeFormatted"
                                :backgroundUrl="otherActivity.backgroundUrl"
                                :start="otherActivity.start.dateTimeString"
                                :end="otherActivity.end.dateTimeString"
                            />
                        </section>
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="$wait.is('fetching view resource')" class="grid">
            <div class="col-12">
                <span class="h2">Loading...</span>
            </div>
        </template>
        <template v-else-if="!$wait.is('fetching view resource') && !resource" class="grid">
            <div class="col-12">
                <span class="h2">Unable to load event</span>
                <button class="btn btn--link" @click.prevent="fetchResource">Retry</button>
            </div>
        </template>
    </LayoutList>
</template>

<script>
    import LayoutList from '@layouts/LayoutList.vue';
    import SvgIcon from '../../SvgIcon.vue';
    import API from '../../../api';
    import ResourceView from '../../ResourceView';
    import ActivityCard from "@components/Activities/Card";

    export default {
        name: "EventView",
        mixins: [ResourceView],
        components: {
            SvgIcon,
            LayoutList,
            ActivityCard
        },
        watch: {
            id: function (newVal, oldVal) {
                console.log("this is being called")
                if(newVal !== oldVal) {
                    this.fetchResource();
                }
            }
        },
        computed: {
            otherActivities() {
                return this.resource.activities.filter(activity => {
                    return ['Theatre', 'Gallery', 'Museum', 'Restaurants', 'Bars', 'Pubs', 'Areas', 'Leisure', 'Other Activities'].includes(activity.activityTypeFormatted);
                });
            },

            // split activities array into multiple arrays based on start date
            activitiesByDate() {
                // filter out activites that have the following types: 'Theatre', 'Gallery', 'Museums' and 'Pubs, Bars, and Restaurants'
                const activities = this.resource.activities.filter(activity => {
                    return !['Theatre', 'Gallery', 'Museum', 'Restaurants', 'Bars', 'Pubs', 'Areas', 'Leisure', 'Other Activities'].includes(activity.activityTypeFormatted);
                });

                // group activities by date and order by unixTimestamp
                let dates = activities.reduce((acc, activity) => {
                    const date = activity.start.displayDate;
                    if (!acc[date]) {
                        acc[date] = [];
                    }
                    acc[date].push(activity);
                    return acc;
                }, {});

                // order the dates from oldest to newest with format D, d M Y
                dates = Object.keys(dates).sort((a, b) => {
                    return new Date(a) - new Date(b);
                }).reduce((acc, date) => {
                    acc[date] = dates[date];
                    return acc;
                }, {});

                // order each activity alphabetically via activityTypeFormatted
                for (let date in dates) {
                    dates[date] = dates[date].sort((a, b) => {
                        return a.activityTypeFormatted.localeCompare(b.activityTypeFormatted);
                    });
                }

                return dates;
            }
        },
        data() {
          return {
              resource: {
                  id: -1,
                  eventName: '',
                  location: '',
                  start: Object,
                  end: Object,
                  bilateralStart: {
                      displayDate: '',
                      displayTime: '',
                  },
                  bilateralEnd: {
                      displayDate: '',
                      displayTime: '',
                  },
                  bilateralMeetingRoom: '',
                  backgroundImage: '',
                  activities: [],
              },
              loading: true,
              failed: false,
          }
        },
        methods: {
            stepLink() {
                const eventId = this.id ? this.id : -1;
                return { name: 'edit-member-step', params: { step: 1, event_id: eventId }}
            },
            async fetchResource() {
                return API.getEventById(this.id);
            }
        }
    }
</script>

<style scoped lang="scss">
    .create {
        margin-top: 3rem;
        margin-bottom: 1.5rem;
    }

    #content__header {
        margin-bottom: 0;
    }

    .activity-date, .other-activities {
        margin-top: 3rem;
    }
</style>
